/**
 * Runtime environment variable validation.
 *
 * All VITE_* variables are statically replaced at build time. This module
 * validates them at app startup so misconfigured deployments fail loudly
 * instead of silently misbehaving at runtime.
 *
 * @spec SPEC:ENGINEERING:FRONTEND:ENV-VALIDATION
 *
 * Usage: import { env } from '@/env'
 */
import { z } from 'zod'

// Optional env vars — absent or empty string both resolve to undefined
const optionalString = z.string().optional().transform((s) => s || undefined)
const optionalUrl = z.string().url().optional().transform((s) => s || undefined)

const envSchema = z.object({
  VITE_API_BASE_URL: z.string().url('VITE_API_BASE_URL must be a valid URL'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  // Optional integrations
  VITE_POSTHOG_KEY: optionalString,
  VITE_POSTHOG_HOST: optionalUrl,
  VITE_POSTHOG_PROJECT_ID: optionalString,
  VITE_LAUNCHDARKLY_CLIENT_ID: optionalString,
  VITE_SENTRY_DSN: optionalUrl,
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  console.error('❌ Invalid environment variables:')
  console.error(parsed.error.flatten().fieldErrors)
  throw new Error('Invalid environment variables — check your .env.local file')
}

export const env = parsed.data

export const isDev = env.VITE_APP_ENV === 'development'
export const isProduction = env.VITE_APP_ENV === 'production'
